import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, RefreshCw, Clock, AlertTriangle } from 'lucide-react';
import { useApp } from '../context/AppContext';

const STATUS_MAP = {
  pending:    { label: '待审核', color: 'bg-yellow-100 text-yellow-700' },
  processing: { label: '处理中', color: 'bg-blue-100 text-blue-700' },
  approved:   { label: '已通过', color: 'bg-blue-100 text-blue-700' },
  success:    { label: '已完成', color: 'bg-green-100 text-green-700' },
  rejected:   { label: '已拒绝', color: 'bg-red-100 text-red-700' },
  failed:     { label: '失败',   color: 'bg-red-100 text-red-700' },
};

const UsdtWithdrawPage = () => {
  const { language, currentUser } = useApp();
  const token = localStorage.getItem('token');

  const [list, setList]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [rejectDialog, setRejectDialog] = useState(null); // { id, order_no }
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const API = process.env.REACT_APP_API_BASE_URL || '';

  const request = async (url, options = {}) => {
    const res = await fetch(`${API}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...(options.headers || {})
      }
    });
    return res.json();
  };

  const loadList = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const res = await request(`/api/usdt-withdraw?${params}`);
      if (res.success) {
        setList(res.data);
        setPagination(res.pagination);
      }
    } catch (err) {
      console.error('加载失败:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadList(1); }, [statusFilter]);

  const handleApprove = async (id, order_no) => {
    if (!window.confirm(`确认通过提币申请 ${order_no}？\n审核通过后将立即发起链上转账，不可撤回。`)) return;
    setActionLoading(true);
    try {
      const res = await request(`/api/usdt-withdraw/${id}/approve`, { method: 'POST' });
      if (res.success) {
        alert('审核通过，已提交链上处理');
        loadList(pagination.page);
      } else {
        alert(`操作失败: ${res.message}`);
      }
    } catch (err) {
      alert('操作失败');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) {
      alert('请填写拒绝原因');
      return;
    }
    setActionLoading(true);
    try {
      const res = await request(`/api/usdt-withdraw/${rejectDialog.id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason: rejectReason })
      });
      if (res.success) {
        alert('已拒绝，余额已退回用户');
        setRejectDialog(null);
        setRejectReason('');
        loadList(pagination.page);
      } else {
        alert(`操作失败: ${res.message}`);
      }
    } catch (err) {
      alert('操作失败');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSync = async (id) => {
    try {
      const res = await request(`/api/usdt-withdraw/${id}/sync`);
      if (res.success) {
        loadList(pagination.page);
      }
    } catch (err) {
      console.error('同步失败:', err);
    }
  };

  const formatTs = (ts) => {
    if (!ts) return '-';
    return new Date(ts * 1000).toLocaleString('zh-CN', {
      month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="p-6 space-y-4">
      {/* 头部 */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold">USDT 提币审核</h2>
          <p className="text-xs text-gray-500 mt-1">审核用户提币申请，通过后自动发起链上转账</p>
        </div>
        <button onClick={() => loadList(pagination.page)}
          className="flex items-center gap-1 px-3 py-1.5 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
          <RefreshCw className="w-4 h-4" />
          刷新
        </button>
      </div>

      {/* 状态筛选 */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: 'all',        label: '全部' },
          { key: 'pending',    label: '待审核' },
          { key: 'processing', label: '处理中' },
          { key: 'success',    label: '已完成' },
          { key: 'rejected',   label: '已拒绝' },
          { key: 'failed',     label: '失败' },
        ].map(s => (
          <button key={s.key}
            onClick={() => setStatusFilter(s.key)}
            className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
              statusFilter === s.key
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
            }`}>
            {s.label}
          </button>
        ))}
      </div>

      {/* 列表 */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-12 text-gray-400">暂无提币申请</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {['申请时间', '用户', '提币金额', '手续费', '总扣除', '收款地址', '状态', '操作'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {list.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {formatTs(item.created_at_ts)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-800">
                        {item.customer_name || item.customer_username || `#${item.customer_id}`}
                      </div>
                      {item.customer_tg && (
                        <div className="text-xs text-gray-400">@{item.customer_tg}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-green-600 whitespace-nowrap">
                      ${parseFloat(item.amount).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                      ${parseFloat(item.fee).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-800 whitespace-nowrap">
                      ${parseFloat(item.total_deduct).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-gray-600 max-w-xs">
                      <div className="truncate w-32" title={item.to_address}>
                        {item.to_address}
                      </div>
                      {item.tx_id && (
                        <div className="text-xs text-blue-500 truncate w-32" title={item.tx_id}>
                          tx: {item.tx_id}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        STATUS_MAP[item.status]?.color || 'bg-gray-100 text-gray-600'
                      }`}>
                        {STATUS_MAP[item.status]?.label || item.status}
                      </span>
                      {item.fail_reason && (
                        <div className="text-xs text-red-500 mt-1">{item.fail_reason}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {item.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(item.id, item.order_no)}
                              disabled={actionLoading}
                              className="flex items-center gap-1 px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600 disabled:opacity-50">
                              <CheckCircle className="w-3 h-3" />
                              通过
                            </button>
                            <button
                              onClick={() => { setRejectDialog({ id: item.id, order_no: item.order_no }); setRejectReason(''); }}
                              disabled={actionLoading}
                              className="flex items-center gap-1 px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600 disabled:opacity-50">
                              <XCircle className="w-3 h-3" />
                              拒绝
                            </button>
                          </>
                        )}
                        {['processing', 'approved'].includes(item.status) && (
                          <button
                            onClick={() => handleSync(item.id)}
                            className="flex items-center gap-1 px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600">
                            <RefreshCw className="w-3 h-3" />
                            同步
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 分页 */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t flex justify-between items-center">
            <span className="text-sm text-gray-500">共 {pagination.total} 条</span>
            <div className="flex gap-2">
              <button
                disabled={pagination.page <= 1}
                onClick={() => loadList(pagination.page - 1)}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-gray-50">
                上一页
              </button>
              <span className="px-3 py-1 text-sm text-gray-600">
                {pagination.page} / {pagination.pages}
              </span>
              <button
                disabled={pagination.page >= pagination.pages}
                onClick={() => loadList(pagination.page + 1)}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-gray-50">
                下一页
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 拒绝弹窗 */}
      {rejectDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="p-5 border-b flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <h3 className="font-bold text-gray-800">拒绝提币申请</h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-600">
                订单号：<span className="font-mono">{rejectDialog.order_no}</span>
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  拒绝原因 <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500"
                  placeholder="请填写拒绝原因，用户可见"
                />
              </div>
              <p className="text-xs text-gray-400">
                拒绝后余额将自动退回用户账户
              </p>
            </div>
            <div className="p-5 border-t flex gap-3">
              <button
                onClick={() => setRejectDialog(null)}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                取消
              </button>
              <button
                onClick={handleRejectConfirm}
                disabled={actionLoading}
                className="flex-1 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-50">
                {actionLoading ? '处理中...' : '确认拒绝'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsdtWithdrawPage;